import { TMolhamImage } from "apiTypes";
import MolhamButton from "components/ReuseableCompos/MolhamButton";
import { tragetableFallbackImg } from "config";
import { useModeContext } from "context/ModeContext";
import useMobile from "customHooks/useMobile";
import useRTL from "customHooks/useRTL";
import useTranslation from "next-translate/useTranslation";
import Image from "next/image";
import { useRef, useState } from "react";
import styled from "styled-components";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";

import { A11y, Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
type Props = {
  title: string;
  images: TMolhamImage[];
  isLastUpdates?: boolean;
  onTouchStart?: () => void;
  onTouchEnd?: () => void;
  hasVidId?: string;
  id: string;
};
const TargetableSlider: React.FC<Props> = ({
  title,
  images,
  isLastUpdates,
  onTouchEnd,
  onTouchStart,
  id,
  hasVidId,
}) => {
  const { theme } = useModeContext();
  const { t } = useTranslation("common");
  const isRTL = useRTL();
  const handleTouchStart = () => {
    isLastUpdates && onTouchStart();
  };
  const handleTouchEnd = () => {
    isLastUpdates && onTouchEnd();
  };
  const videoRef = useRef(null);
  const isMobile = useMobile("md");
  const unmute = () => {
    videoRef.current.muted = false;
    videoRef.current.volume = 0.15;
    setMuted(false);
  };
  const [muted, setMuted] = useState(true);
  return (
    <div className="mt-0 mt-lg-3">
      <StyledSwiper
        key={isRTL.toString()}
        modules={[Navigation, Pagination, A11y]}
        spaceBetween={20}
        slidesPerView={1}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        navigation
        dir={isRTL ? "rtl" : "ltr"}
        theme={theme}
      >
        {hasVidId && (
          <SwiperSlide key={hasVidId} className=" position-relative">
            <video
              ref={videoRef}
              src={
                "https://cdn.molhamteam.com/assets/videos/" + hasVidId + ".mp4"
              }
              controls
              playsInline
              autoPlay
              height={isMobile ? 205 : 500}
              loop
              muted
              className="w-100"
              style={isMobile ? { objectFit: "contain" } : null}
            >
              {/* tap to unmute */}
            </video>
            {muted && (
              <MolhamButton
                id="unmute-button"
                label={t("tap_to_unmute")}
                endIcon="fe-volume-2"
                onClick={unmute}
                className="position-absolute zIndex9999"
                style={{
                  top: "50%",
                  left: "50%",
                  transform: "translate(-50%,-50%)",
                }}
              />
            )}
          </SwiperSlide>
        )}

        {images?.length <= 0 && !isLastUpdates ? (
          <Image
            unoptimized
            blurDataURL={tragetableFallbackImg}
            height={900}
            width={1600}
            placeholder="blur"
            loading="eager"
            className={"image"}
            style={{
              aspectRatio: "16 / 9",
            }}
            src={tragetableFallbackImg}
            alt={title}
          />
        ) : (
          images?.map((item, idx) => {
            return (
              <SwiperSlide key={idx}>
                <Image
                  unoptimized
                  loading="eager"
                  blurDataURL={item?.blurry?.["16:9"]}
                  height={900}
                  width={1600}
                  placeholder="blur"
                  style={{
                    aspectRatio: isMobile ? "4/3" : "16 / 9",
                    objectFit: "cover",
                  }}
                  className={"image"}
                  src={
                    id === "651" && !isRTL
                      ? "https://cdn.molhamteam.com/assets/651.jpg"
                      : item.original ?? item.thumbnails["16:9"]
                  }
                  alt={title}
                />
              </SwiperSlide>
            );
          })
        )}
      </StyledSwiper>
    </div>
  );
};

export default TargetableSlider;

const StyledSwiper = styled(Swiper)`
  .swiper-pagination {
    width: 100%;
    text-align: center !important;
    bottom: 0 !important;
    @media (max-width: 768px) {
      bottom: 12px !important;
    }
  }

  .swiper-pagination-bullet {
    background: ${({ theme }) =>
      theme === "light" ? "var(--primary2)" : "#fff"} !important;
    margin-inline: 8px !important;
    opacity: 1 !important;
    @media (max-width: 767.98px) {
      background: #fff !important;
    }
  }
  .swiper-pagination-bullet-active {
    background: transparent !important;
    border: 1px solid
      ${({ theme }) => (theme === "light" ? "var(--primary2)" : "#fff")} !important;
    transform: scale(2) !important;
    @media (max-width: 767.98px) {
      background: transparent !important;
      border: 1px solid #fff !important;
    }
  }

  .swiper-button-prev,
  .swiper-button-next {
    top: calc(50%) !important;
    background: #fff !important;
    border-radius: 25% !important;
    width: 50px !important;
    height: 50px !important;
    @media (max-width: 767.98px) {
      display: none !important;
    }
  }
  .swiper-button-prev::after {
    margin-inline-end: 4px !important;
  }

  .swiper-button-next {
    margin-inline-start: 4px !important;
  }

  .swiper-button-prev::after,
  .swiper-button-next::after {
    color: var(--primary2) !important;

    font-size: 25px !important;
  }

  .swiper-button-disabled {
    display: none !important;
  }

  .image {
    border-radius: 1rem;
  }
  @media (max-width: 991.98px) {
    .image {
      border-radius: 0;
    }
  }
`;
